/**
 * The places a gardener can be found: their marks and their colours, and the
 * handles the server says may be printed.
 *
 * A handle on its own is not enough context. "@puzzlemonster" printed on a
 * shared photograph could be anywhere, and a label that says "Instagram" in
 * words is bigger than the handle it describes. A tiny mark in the platform's
 * own colour is what everyone already reads.
 *
 * The marks and colours come from Simple Icons, which keeps them current and
 * is tree-shaken to the six used here. They are kept as raw path data
 * because the share frame draws onto a canvas and needs the geometry rather
 * than an element; the SocialIcon component wraps the same data for the DOM.
 *
 * What a handle is, how it is cleaned and checked, its address and how it is
 * printed ("@amy") are the server's (E7, owner 2026-10-26: "this is business
 * logic. It belongs on the back end."): the account sends profile_links and a
 * member's garden sends the owner's (GET /garden-place ownerShare.links), and
 * linksOf only keeps the ones the server says may be printed.
 *
 * Who uses it: the profile's links (ProfileLinksCard, PublicPageCard), the
 * share pictures (shareFrames, shareStyle, seasonFrame, useShareHandles), the
 * public pages (gardenPageParts) and api/gardens and api/shares (LinkKey).
 *
 * @module socialLinks
 */

import { siFacebook, siInstagram, siPinterest, siTiktok, siX, siYoutube } from 'simple-icons';

/**
 * The link fields on the profile, in the server's order (services/gardens/profileLinks LINK_KEYS): every place
 * that has a mark here. The Links screen draws the boxes the server sends.
 */
export const LINK_KEYS = ['website', 'instagram', 'facebook', 'pinterest', 'youtube', 'tiktok', 'x'] as const;
/** One of the profile's link fields. */
export type LinkKey = (typeof LINK_KEYS)[number];

/** A place's mark: its name, its geometry and its colour. */
export interface SocialMark {
  /** The platform's own name, for labels and alt text. */
  title: string;
  /** SVG path data on a 24×24 box. */
  path: string;
  /** The brand colour as a CSS hex string, or 'currentColor' for a black mark. */
  color: string;
}

/**
 * A plain globe for a website, drawn to match the others' weight.
 *
 * Simple Icons has no mark for "a website", and a wordmark would be wrong: it
 * is the one place that is the gardener's own rather than a platform's.
 */
const GLOBE_PATH =
  'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm6.9 6h-2.95a15.7 15.7 0 0 0-1.38-3.56A8.03 8.03 0 0 1 18.9 8zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14A7.9 7.9 0 0 1 4 12c0-.69.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56A7.99 7.99 0 0 1 5.08 16zm2.95-8H5.08a7.99 7.99 0 0 1 4.33-3.56A15.7 15.7 0 0 0 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2s.07-1.35.16-2h4.68c.09.65.16 1.32.16 2s-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h2.95a8.03 8.03 0 0 1-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z';

/** Each place's mark, by its link field. The website's green is the app's own. */
export const SOCIAL_MARKS: Record<LinkKey, SocialMark> = {
  website: { title: 'Website', path: GLOBE_PATH, color: '#2D5A27' },
  instagram: { title: siInstagram.title, path: siInstagram.path, color: `#${siInstagram.hex}` },
  facebook: { title: siFacebook.title, path: siFacebook.path, color: `#${siFacebook.hex}` },
  pinterest: { title: siPinterest.title, path: siPinterest.path, color: `#${siPinterest.hex}` },
  youtube: { title: siYoutube.title, path: siYoutube.path, color: `#${siYoutube.hex}` },
  // TikTok's mark is black, which vanishes on a dark theme and on a
  // photograph's scrim alike; it is drawn in whatever colour surrounds it.
  tiktok: { title: siTiktok.title, path: siTiktok.path, color: 'currentColor' },
  // X's mark is black too: drawn in the colour around it, as TikTok's.
  x: { title: siX.title, path: siX.path, color: 'currentColor' },
};

/** One link box as the server reads it (the account's profile_links; E7). */
export interface ProfileLink {
  /** The box. */
  key: LinkKey;
  /** What the box shows: the bare handle (a website's address); a saved value that fails, as saved; '' for none. */
  value: string;
  /** What a share picture prints ("@amy", "yourgarden.com/blog"); null for none or a value that fails. */
  display: string | null;
  /** The profile address; null for none or a value that fails. */
  url: string | null;
  /** Why a saved value is not used, in the reader's language; null when it is. */
  problem: string | null;
}

/** A link a page may print, as the server sends it (a member's ownerShare.links: the owner's that pass). */
export interface ShownLink {
  /** The place. */
  key: LinkKey;
  /** What is printed ("@amy"). */
  display: string | null;
  /** The profile address. */
  url?: string | null;
}

/**
 * The links a share may print, in the server's order: those the server gave a printed form (display). A box that is
 * empty, or a saved value that fails the platform's rules, has none and is left out.
 *
 * @param links - The account's profile_links, or a member's ownerShare.links; nothing yet.
 * @returns Each printable place and the handle to print.
 */
export function linksOf(
  links: ReadonlyArray<ShownLink> | null | undefined
): Array<{ key: LinkKey; handle: string }> {
  if (!links) return [];
  return links
    .filter((link): link is ShownLink & { display: string } => typeof link.display === 'string' && link.display !== '')
    .filter((link) => (LINK_KEYS as readonly string[]).includes(link.key))
    .map((link) => ({ key: link.key, handle: link.display }));
}
